import { Analytics } from '@util/Analytics';
import { Environment } from '@util/Environment';
import { useQuery, userIdFromQueryParams } from '@util/QueryParams';
import { isDirectToPayRoute } from '@util/RouterUtils';
import React, { ReactElement, useCallback, useEffect } from 'react';
import { isEmpty } from 'ts-utils';
import { ScreenConfigBase, ScreenType } from '../../config/FlowTypes';
import { ActionType } from '../../store/ActionTypes';
import { store, useAppDispatch, useAppStore } from '../../store/Store';
import { getDirectMedicareCouponCode } from '../../util/MedicareAccess';
import { LoadingSpinner } from '../styles/LoadingSpinner';
import { NavigationProps } from './FlowNavigation';
import { PaymentLayout } from './layouts/PaymentLayout';

export type PaymentScreenConfig<ScreenNames extends string> = ScreenConfigBase<
  ScreenType.Payment,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  PaymentScreenConfig<ScreenNames>,
  ScreenNames
>;

export const PaymentScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const dispatch = useAppDispatch();
  const query = useQuery();
  const user = useAppStore((state) => state.user);
  const userIdFromQueryString = userIdFromQueryParams(query);
  const userId = user?.userId ?? userIdFromQueryString;
  const userEmail = user?.email;
  const userError = user?.error;
  const userNeedsSetup = !isEmpty(userId) && isEmpty(userEmail);

  useEffect(function applyDirectMedicareDiscount() {
    const couponCode = getDirectMedicareCouponCode(store.getState());
    if (couponCode !== undefined) {
      dispatch({ type: ActionType.SET_COUPON_CODE, couponCode });
    }
  }, []);

  useEffect(
    function setupUser() {
      if (userNeedsSetup) {
        // To handle /start flow with userId passed in as a query param
        dispatch({ type: ActionType.SETUP_INCOMPLETE_USER, userId });
      }
    },
    [userNeedsSetup]
  );

  useEffect(
    function identifyUser() {
      // To handle /start flow with userId passed in as a query param
      if (isDirectToPayRoute() && !isEmpty(userId)) {
        Analytics.identify({ userId }, Analytics.Event.o_payment_opened);
      }
    },
    [userId]
  );

  useEffect(
    function goHomeOnAuthError() {
      if (userError !== undefined) {
        window.location.href = Environment.DEVELOPMENT ? `/${Environment.APP}` : '/';
      }
    },
    [userError]
  );

  const onNext = useCallback(() => {
    props.navigation.next(props, { resetHistory: true });
  }, [props]);

  return userNeedsSetup ? <LoadingSpinner /> : <PaymentLayout nextPage={onNext} />;
};
